// PeriodInside — the inside of a PERIOD filter (ADR-082 §4 · §5, on the
// family's shape — ADR-080 §11–§13): the surface's WORDS as a short list, the
// exact start or the range each one means under it ("since Sat 4 Oct" ·
// "5–11 Oct"), "Custom…" the acting last row. Custom swaps the words for the
// RANGE face in the same place — two typed day fields over the inline range
// calendar, Apply — inside the filter's panel on a desk and inside the drawer's
// page on a phone (never a sheet over the drawer; the back row returns to the
// words). The value is ONE string as the URL carries it: a word (rolling), or
// `YYYY-MM-DD..YYYY-MM-DD` written only once both days are picked (fixed). A
// chosen value the words do not list — a pair, a word this surface hides that
// arrived by a link — leads the list, named, so the inside always shows what
// the chip says; a re-press of the chosen row clears (a filter's rule). The
// captions read the screen's zone and the read's clock (`PeriodSpec`); nothing
// here decides a query's bounds — the server resolves the word (ADR-082 §6).

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button as AriaButton } from 'react-aria-components';
import { formatDayPair, parsePeriod, type CalendarDay } from '@ems-app/schemas/common/period';
import type { OptionRow } from '../../../hooks/useOptions.js';
import { periodCaption, periodDays, periodValueLabel, periodWordLabel, type PeriodContext } from '../../../lib/period.js';
import { Button } from '../../ui/Button.js';
import { DatePicker, RangeCalendarInline } from '../../ui/DatePicker.js';
import { icons } from '../../ui/icons.js';
import { PickerList } from '../../ui/PickerList.js';
import type { PeriodSpec } from './types.js';

export interface PeriodInsideProps {
  spec: PeriodSpec;
  /** The filter's name — the list's and the calendar's accessible name. */
  label: string;
  /** The value as the kit stores every dimension — at most one string. */
  value: string[];
  onChange: (next: string[]) => void;
  /** After a pick or an Apply — the host closes its panel, or goes back a page. */
  onDone?: () => void;
  /** `touch` = finger-sized rows (the phone drawer). */
  rowSize?: 'default' | 'touch';
}

/** The way back to the words — a desk row, or a finger-sized one on the phone drawer (ADR-080 §12). */
const backRow = (touch: boolean): string =>
  `mb-1 flex w-full items-center gap-1.5 rounded-md px-2 ${touch ? 'min-h-11 text-base' : 'py-1.5 text-xs'} font-medium text-[var(--color-text-secondary)] outline-none focus-ring-inset data-[hovered]:text-[var(--color-text-primary)]`;

export function PeriodInside({ spec, label, value, onChange, onDone, rowSize = 'default' }: PeriodInsideProps) {
  const { t } = useTranslation('common');
  const ctx: PeriodContext = { zone: spec.zone, weekStart: spec.weekStart, now: spec.now };
  const current = value[0] ?? null;
  const [face, setFace] = useState<'words' | 'custom'>('words');

  if (face === 'custom') {
    return (
      <CustomFace
        spec={spec}
        ctx={ctx}
        label={label}
        current={current}
        touch={rowSize === 'touch'}
        onBack={() => setFace('words')}
        onApply={(pair) => {
          onChange([pair]);
          onDone?.();
        }}
      />
    );
  }

  const rows: OptionRow[] = spec.words.map((w) => ({ id: w, label: periodWordLabel(w, t), description: periodCaption(w, ctx, t) ?? undefined, pickable: true, group: null }));
  // A chosen value the words do not list leads the list, named (a pair · a hidden word from a link).
  if (current !== null && !rows.some((r) => r.id === current)) {
    const p = parsePeriod(current);
    const description = p === null ? undefined : typeof p === 'object' ? t('period.customRange') : (periodCaption(p, ctx, t) ?? undefined);
    rows.unshift({ id: current, label: periodValueLabel(current, ctx, t), description, pickable: true, group: null });
  }
  return (
    <PickerList
      rows={rows}
      value={value}
      // A pick (or a re-press, which empties — the filter clears) closes the host; the tail stays open (it swaps the face).
      onChange={(next) => {
        onChange(next.slice(0, 1));
        onDone?.();
      }}
      tail={{ label: t('period.custom'), icon: <icons.calendar aria-hidden size={16} />, onAction: () => setFace('custom') }}
      ariaLabel={label}
      rowSize={rowSize}
    />
  );
}

/** The range face: From · To typed, the calendar below, Apply — pre-filled with the current period's two days. */
function CustomFace({
  spec,
  ctx,
  label,
  current,
  touch,
  onBack,
  onApply,
}: {
  spec: PeriodSpec;
  ctx: PeriodContext;
  label: string;
  current: string | null;
  touch: boolean;
  onBack: () => void;
  onApply: (pair: string) => void;
}) {
  const { t } = useTranslation('common');
  const [draft, setDraft] = useState<{ first: CalendarDay | null; last: CalendarDay | null }>(() => {
    const p = current === null ? null : parsePeriod(current);
    const days = p === null ? null : periodDays(p, ctx);
    return days ? { first: days.first, last: days.last } : { first: null, last: null };
  });
  const ready = draft.first !== null && draft.last !== null && draft.first <= draft.last;
  const range = ready ? { start: draft.first!, end: draft.last! } : null;
  // The face swapped under the keyboard's focus: land it on the back row (never a field — a phone would raise its keyboard over the calendar).
  const backRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    backRef.current?.focus();
  }, []);
  // The grid follows the typed start's month (the calendar seeds its visible month once, at mount).
  const focusedDay = draft.first ?? draft.last ?? undefined;
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <AriaButton ref={backRef} onPress={onBack} className={backRow(touch)}>
        <icons.caretLeft aria-hidden size={12} className="rtl:rotate-180" />
        {t('period.customRange')}
      </AriaButton>
      <div className="flex gap-2 px-1">
        <DatePicker calendar={false} label={t('period.from')} value={draft.first} onChange={(v) => setDraft((d) => ({ ...d, first: v }))} maxValue={draft.last ?? undefined} className="min-w-0 flex-1" />
        <DatePicker calendar={false} label={t('period.to')} value={draft.last} onChange={(v) => setDraft((d) => ({ ...d, last: v }))} minValue={draft.first ?? undefined} className="min-w-0 flex-1" />
      </div>
      <div className="@container flex justify-center px-1 py-2">
        <RangeCalendarInline
          key={focusedDay?.slice(0, 7) ?? ''}
          ariaLabel={label}
          value={range}
          focusedDay={focusedDay}
          onChange={(v) => setDraft(v ? { first: v.start, last: v.end } : { first: null, last: null })}
          weekStart={spec.weekStart}
          zone={spec.zone}
          touch={touch}
        />
      </div>
      <div className="px-1 pb-1">
        <Button variant="primary" className="w-full" disabled={!ready} onClick={() => ready && onApply(formatDayPair({ first: draft.first!, last: draft.last! }))}>
          {t('period.apply')}
        </Button>
      </div>
    </div>
  );
}
